import { z } from 'zod';

export const MODEL_RUNTIME_POLICY_PURPOSES = [
  'interactive',
  'background',
  'evaluation',
  'daemon',
  'workflow',
  'debug',
  'bulk',
] as const;

export const MODEL_RUNTIME_RISK_TIERS = ['low', 'medium', 'high', 'privileged'] as const;
export const MODEL_RUNTIME_LATENCY_CLASSES = ['realtime', 'interactive', 'batch'] as const;
export const MODEL_RUNTIME_COST_TIERS = ['free', 'low', 'medium', 'high'] as const;
export const MODEL_RUNTIME_PERMISSION_TIERS = ['observer', 'contributor', 'operator'] as const;
export const MODEL_RUNTIME_PERMISSION_MODES = [
  'provider_default',
  'read_only',
  'workspace_write',
  'full_auto',
  'bypass_permissions',
] as const;
export const MODEL_RUNTIME_SESSION_MODES = ['provider_default', 'stateless', 'reuse_session'] as const;

/**
 * The kinds of work a runtime policy runs (owner 2026-10-14): each has a list of allowed policies and a default, kept in
 * `runtime_policy_surface_rules` — the system's row, or an organization's row that replaces it. The allowed policies
 * are the only limit on what may run a piece of work; a person assigns any of them. Generic kinds: a module maps its
 * own calls onto one, never names itself here.
 */
export const RUNTIME_POLICY_SURFACES = [
  /** A person's conversation turn, a persona asked a question, the platform consult. */
  'conversation',
  /** An employee's (or a System daemon's) run. */
  'workforce',
  /** A workflow step. */
  'workflow',
  /** Content analysis: media, profiles, digests, training sets, extraction. */
  'analysis',
  /** An automated evaluation's candidate answers (the persona gate, harness drivers). */
  'evaluation',
  /** A judge grading an evaluation. */
  'judge',
  /** Code writing and code transformation. */
  'codegen',
  /** Internal helpers: titles, compaction, summaries, intent classification, entity resolution. */
  'system',
  /**
   * General web search and reading (TA desk U3a, owner 2026-10-24): a policy whose CLI may use only its WebSearch and
   * WebFetch tools. It names leads (URLs); the pages themselves are read verbatim by the organization's worker.
   */
  'research',
] as const;
export type RuntimePolicySurface = (typeof RUNTIME_POLICY_SURFACES)[number];

/** Whether a reasoning model thinks before it answers under the policy (finance-e2e D68); `model_default` leaves it. */
export const MODEL_RUNTIME_POLICY_THINKING = ['model_default', 'on', 'off'] as const;
export type ModelRuntimePolicyThinking = (typeof MODEL_RUNTIME_POLICY_THINKING)[number];

/** One model of a policy's ordered list, by its `models.id` (the slug rides along for the worker wire and display). */
export const RuntimePolicyModelSchema = z.object({
  id: z.number().int().positive(),
  slug: z.string().min(1),
});
export type RuntimePolicyModel = z.infer<typeof RuntimePolicyModelSchema>;

export const ModelRuntimePolicySnapshotSchema = z.object({
  id: z.number().nullable(),
  organizationId: z.number().nullable(),
  slug: z.string(),
  name: z.string(),
  version: z.number().int().positive(),
  purpose: z.enum(MODEL_RUNTIME_POLICY_PURPOSES),
  riskTier: z.enum(MODEL_RUNTIME_RISK_TIERS),
  latencyClass: z.enum(MODEL_RUNTIME_LATENCY_CLASSES),
  costTier: z.enum(MODEL_RUNTIME_COST_TIERS),
  permissionTier: z.enum(MODEL_RUNTIME_PERMISSION_TIERS),
  tags: z.array(z.string()),
  wallClockTimeoutMs: z.number().int().positive(),
  providerRequestTimeoutMs: z.number().int().positive(),
  idleTimeoutMs: z.number().int().positive().nullable(),
  maxInputTokens: z.number().int().positive().nullable(),
  maxOutputTokens: z.number().int().positive(),
  maxSystemPromptChars: z.number().int().positive().nullable(),
  /**
   * At most this many of the policy's calls run on one model server at once; it only narrows the model's own
   * `max_concurrent_requests` (the worker takes the smaller, `model-capacity-gate.ts`). Null: the model's limit alone.
   * Absent from a snapshot written before 2026-10-24.
   */
  maxConcurrentRequests: z.number().int().positive().nullable().optional(),
  maxToolCalls: z.number().int().nonnegative(),
  maxTurns: z.number().int().positive(),
  retryMax: z.number().int().nonnegative(),
  retryBackoffMs: z.number().int().nonnegative(),
  retryBackoffMultiplier: z.number().positive(),
  retryJitter: z.boolean(),
  spendCapUsd: z.number().nonnegative().nullable(),
  permissionMode: z.enum(MODEL_RUNTIME_PERMISSION_MODES),
  allowedProviderTools: z.array(z.string()),
  streamingEnabled: z.boolean(),
  sessionMode: z.enum(MODEL_RUNTIME_SESSION_MODES),
  promptCachingEnabled: z.boolean(),
  providerExtensions: z.record(z.string(), z.unknown()),
  /** The models that run the policy's work, in order: the first a live worker serves for the call answers. */
  models: z.array(RuntimePolicyModelSchema).default([]),
  /** The policy that answers when none of these models is served (a conversation's local fallback); null = none. */
  fallbackPolicyId: z.number().int().positive().nullable().default(null),
  thinking: z.enum(MODEL_RUNTIME_POLICY_THINKING).default('model_default'),
});

export type ModelRuntimePolicySnapshot = z.infer<typeof ModelRuntimePolicySnapshotSchema>;
export type ModelRuntimePolicyPurpose = (typeof MODEL_RUNTIME_POLICY_PURPOSES)[number];
export type ModelRuntimePolicyRiskTier = (typeof MODEL_RUNTIME_RISK_TIERS)[number];
export type ModelRuntimePolicyLatencyClass = (typeof MODEL_RUNTIME_LATENCY_CLASSES)[number];
export type ModelRuntimePolicyCostTier = (typeof MODEL_RUNTIME_COST_TIERS)[number];
export type ModelRuntimePolicyPermissionTier = (typeof MODEL_RUNTIME_PERMISSION_TIERS)[number];

/**
 * What a piece of work asks for: the policy it carries (its own assignment, or its parent's) on its surface, or —
 * none carried — the surface's default. A carried policy must be one the surface allows for the organization.
 */
export interface ModelRuntimePolicyRequest {
  organizationId?: number;
  surface: RuntimePolicySurface;
  policyId?: number;
}

/**
 * The runtime policy a run carries in its context records (owner 2026-10-14: "a slice of work can pass the parent
 * policy or be assigned its own") — `spawnContext`, `contextSpec`, a workflow run's `context` — under two keys, so
 * every hop reads it the same way. Absent policy id: the surface's default.
 */
export interface RunRuntimePolicy {
  policyId?: number;
  surface: RuntimePolicySurface;
}

/** The context key holding the policy id the run carries. */
export const RUN_POLICY_ID_KEY = 'runtimePolicyId';
/** The context key holding the kind of work the run is. */
export const RUN_SURFACE_KEY = 'runtimeSurface';

export function isRuntimePolicySurface(value: unknown): value is RuntimePolicySurface {
  return typeof value === 'string' && (RUNTIME_POLICY_SURFACES as readonly string[]).includes(value);
}

/** The policy a context record carries, when it carries one. */
export function runPolicyOf(context: Readonly<Record<string, unknown>> | null | undefined): RunRuntimePolicy | null {
  if (!context) return null;
  const surface = context[RUN_SURFACE_KEY];
  if (!isRuntimePolicySurface(surface)) return null;
  const policyId = context[RUN_POLICY_ID_KEY];
  return typeof policyId === 'number' && Number.isInteger(policyId) && policyId > 0
    ? { policyId, surface }
    : { surface };
}

/** The two context keys a child's record carries to inherit `policy`. */
export function runPolicyContext(policy: RunRuntimePolicy): Record<string, unknown> {
  return {
    [RUN_SURFACE_KEY]: policy.surface,
    ...(policy.policyId != null ? { [RUN_POLICY_ID_KEY]: policy.policyId } : {}),
  };
}

/** Test fixture only; runtime resolution reads the persisted policies (`model-runtime-policy-store.ts`). */
export const DEFAULT_MODEL_RUNTIME_POLICY: ModelRuntimePolicySnapshot = {
  id: null,
  organizationId: null,
  slug: 'interactive-standard',
  name: 'Interactive Standard',
  version: 1,
  purpose: 'interactive',
  riskTier: 'medium',
  latencyClass: 'interactive',
  costTier: 'medium',
  permissionTier: 'observer',
  tags: ['fallback'],
  // Generous time ceilings: slow, free, locally-hosted LLMs may run continuous
  // research for hours (incl. fan-out). Paid models stay bounded by spendCapUsd +
  // the provider CLI's own budget/timeout — the cost GATE limits them, not the clock.
  wallClockTimeoutMs: 14_400_000, // 4h
  providerRequestTimeoutMs: 3_600_000, // 1h per LLM call
  idleTimeoutMs: 1_800_000, // 30m
  maxInputTokens: null,
  // A reasoning model's thinking counts toward its output budget: a tool-calling turn needs room (finance-e2e D60).
  maxOutputTokens: 16384,
  maxSystemPromptChars: null,
  maxConcurrentRequests: null,
  maxToolCalls: 500,
  maxTurns: 200,
  retryMax: 1,
  retryBackoffMs: 1000,
  retryBackoffMultiplier: 2,
  retryJitter: true,
  spendCapUsd: 5,
  permissionMode: 'provider_default',
  allowedProviderTools: [],
  streamingEnabled: true,
  sessionMode: 'provider_default',
  promptCachingEnabled: true,
  providerExtensions: {},
  models: [],
  fallbackPolicyId: null,
  thinking: 'model_default',
};

/** The `ai:complete` thinking flag a policy asks for: absent leaves the model's own default. */
export function thinkingFlagOf(policy: Pick<ModelRuntimePolicySnapshot, 'thinking'>): boolean | undefined {
  if (policy.thinking === 'on') return true;
  if (policy.thinking === 'off') return false;
  return undefined;
}

/** How many fallback hops a policy chain may take before it is treated as broken configuration. */
export const RUNTIME_POLICY_FALLBACK_DEPTH_MAX = 4;

export type RuntimePolicyErrorCode =
  | 'surface_rule_missing'
  | 'not_allowed'
  | 'not_found'
  | 'fallback_cycle'
  | 'fallback_too_deep';

/** A policy that cannot run the work as asked — a configuration or assignment problem, never a transient one. */
export class RuntimePolicyError extends Error {
  constructor(
    public readonly code: RuntimePolicyErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'RuntimePolicyError';
  }
}
